// Build-time markdown → HTML for the legal pages (plan/04 §4). `/legal/*` routes
// inherit `prerender = true` from the root layout, so `marked.parse` runs once at
// build time and the output is baked into static HTML — no client-side cost, and
// no untrusted input ever reaches it (every source file is first-party prose in
// `lib/content/legal/`, read via a Vite `?raw` import).
import { marked } from 'marked';

/** kebab-case slug for heading anchors (e.g. "Cookies and analytics" → "cookies-and-analytics"). */
function slugify(text: string): string {
	return text
		.replace(/<[^>]+>/g, '')
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, '')
		.replace(/\s+/g, '-');
}

// Emit heading ids so in-page anchors resolve (the consent banner deep-links to
// the privacy policy's cookies/analytics section; SvelteKit's prerender crawler
// also validates these fragments).
marked.use({
	renderer: {
		heading({ tokens, depth }) {
			const content = this.parser.parseInline(tokens);
			return `<h${depth} id="${slugify(content)}">${content}</h${depth}>\n`;
		}
	}
});

export interface LegalDoc {
	/** The document's single `# ` heading — rendered as the page's real <h1>. */
	title: string;
	/** The `_Last updated …_` line, pulled out for its own mono treatment. */
	updated: string | null;
	/** Everything else, rendered to HTML (## → h2, lists, tables, links, …). */
	bodyHtml: string;
}

const TITLE_RE = /^#\s+(.+)$/m;
const UPDATED_RE = /^_Last updated (.+?)\._\s*$/m;

/** Splits a legal markdown source into title / last-updated / rendered body. */
export function parseLegalDoc(raw: string): LegalDoc {
	const titleMatch = raw.match(TITLE_RE);
	const updatedMatch = raw.match(UPDATED_RE);

	// Both lines are lifted out and given their own typographic treatment
	// (h1 + mono date) rather than flowing as default markdown prose.
	const rest = raw.replace(TITLE_RE, '').replace(UPDATED_RE, '').trim();

	return {
		title: titleMatch?.[1]?.trim() ?? '',
		updated: updatedMatch?.[1]?.trim() ?? null,
		bodyHtml: marked.parse(rest, { async: false })
	};
}
